import { memo, useEffect, useState, useRef, useMemo } from 'react';
import { isEmpty, isUndefined, isFunction, isString } from 'lodash';
import classNames from 'classnames';
// Import Types
import type { TabConfig } from 'types';
// Import Hooks
import { useHasPermission } from 'hooks';
// Import Components
import CustomTab from './CustomTab';
import AccessControl from 'components/common/AccessControl';
// Import Styles
import 'assets/scss/customTabs.scss';

const getTabKey = (tabKey, i) => (isUndefined(tabKey) ? i : tabKey);

interface Props {
    tabsConfig: TabConfig[];
    activeTabKey?: string | number;
    tabChangeHandler?: (key: string | number) => void;
    customCssClassNames?: string;
    isValid?: boolean[];
    isDisabled?: boolean;
}

const CustomTabs = (props: Props) => {
    const {
        tabsConfig,
        activeTabKey = '0',
        tabChangeHandler,
        customCssClassNames = '',
        isValid = [],
        isDisabled = false,
    } = props;

    const { hasSubscriptionSection } = useHasPermission();

    const [activeKey, setActiveKey] = useState(activeTabKey);
    const isOpened = useRef(new Set([activeTabKey]));

    const tabClickHandler = (key, isExecutedExternally = false) => {
        return () => {
            if (key === activeKey) return;

            isOpened.current.add(key);

            if (isFunction(tabChangeHandler) && !isExecutedExternally) {
                tabChangeHandler(key);
            } else {
                setActiveKey(key);
            }
        };
    };

    useEffect(() => {
        if (activeTabKey !== activeKey) {
            tabClickHandler(activeTabKey, true)();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTabKey]);

    const filteredTabsConfig = useMemo(() => {
        return tabsConfig
            .map((tab, originalIndex) => ({ ...tab, originalIndex }))
            .filter(
                ({ subscriptionSection }) =>
                    isEmpty(subscriptionSection) || hasSubscriptionSection(subscriptionSection),
            );
    }, [hasSubscriptionSection, tabsConfig]);

    useEffect(() => {
        const availableKeys = filteredTabsConfig.map(({ tabKey, originalIndex }) => getTabKey(tabKey, originalIndex));
        if (availableKeys.length && !availableKeys.includes(activeKey)) {
            tabClickHandler(availableKeys[0], true)();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredTabsConfig]);

    return (
        <>
            {!isEmpty(tabsConfig) && (
                <div
                    className={
                        !isEmpty(customCssClassNames) && isString(customCssClassNames)
                            ? customCssClassNames
                            : 'crm-custom-tabs-cnt'
                    }
                >
                    <ul
                        className={classNames('crm-custom-tabs-btn-panel', {
                            disabled: isDisabled,
                        })}
                    >
                        {filteredTabsConfig.map(
                            (
                                {
                                    tabKey,
                                    tabTitle,
                                    hasSwitcher,
                                    switchChangeHandler,
                                    switcherChecked,
                                    isDisabled,
                                    permissions,
                                    partnerSetting,
                                    originalIndex,
                                },
                                i,
                            ) => (
                                <AccessControl permissions={permissions} partnerSetting={partnerSetting} key={i}>
                                    <CustomTab
                                        tabKey={isDisabled ? null : tabKey}
                                        tabTitle={tabTitle}
                                        hasSwitcher={hasSwitcher}
                                        switchChangeHandler={switchChangeHandler}
                                        switcherChecked={switcherChecked}
                                        getTabKey={getTabKey}
                                        activeKey={activeKey}
                                        tabClickHandler={tabClickHandler}
                                        index={originalIndex}
                                        isValid={isEmpty(isValid) || isValid[originalIndex]}
                                        isDisabled={isDisabled}
                                    />
                                </AccessControl>
                            ),
                        )}
                        <div className="tab-switcher" />
                    </ul>
                    <div className="crm-custom-tabs-content-wrapper">
                        <div className="crm-custom-tabs-content">
                            {filteredTabsConfig.map(
                                ({ tabKey, tabContent, permissions, partnerSetting, originalIndex }, i) => {
                                    return (
                                        <AccessControl
                                            permissions={permissions}
                                            partnerSetting={partnerSetting}
                                            key={i}
                                        >
                                            <div
                                                className={classNames('crm-tab-content', {
                                                    active: getTabKey(tabKey, originalIndex) === activeKey,
                                                })}
                                            >
                                                {isOpened.current.has(getTabKey(tabKey, originalIndex)) && tabContent}
                                            </div>
                                        </AccessControl>
                                    );
                                },
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default memo(CustomTabs);
